import { ReturnOrderListItemV2 } from 'services/api/refund';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import { DetailCard } from '../DetailCard';
import {
  useReturnItemStatusTip,
  useTikTokTimeTip,
} from 'views/Refund/components/ReturnListItem/hooks/useReturnItemDisplayState';
import { useMemo } from 'react';
import { InfoBox } from '../OrderDetail/InfoBox';
import { useTranslate } from '@tolgee/react';
import { CopyText } from '../CopyText';
import { useRefundDrawerStatusTip } from 'views/Refund/components/RefundDrawer/hooks/useRefundDrawerStatusTip';
import { useRefundStatusContent } from 'views/Refund/components/RefundDrawer/hooks/useRefundDrawerBody';
import { isTiKTok } from 'lib/utils/isTikTok';
import dayjs from 'lib/dayjs';
import { useReturnItemOperatable } from 'views/Refund/components/ReturnListItem/hooks/useReturnItemPermissions';

interface OrderReturnInfoProps {
  platform: General.PlatformUpperCase;
  returnOrder: ReturnOrderListItemV2;
}

const ShopeeReturnInfo = (props: OrderReturnInfoProps & { returnStatusTip: string }) => {
  const { platform: platformUpperCase, returnStatusTip, returnOrder } = props;
  const platform = toLowerCase(platformUpperCase);
  const { t } = useTranslate();

  const {
    returnSn,
    returnTrackingNumber,
    displayStatus,
    dueDateMs,
    unit,
    partiallyRefundAmount,
    refundAmount,
    latestOfferAmount,
    compensationAmount,
    sellerEvidenceDeadlineMs,
    returnShipDueDateMs,
    sellerCompensationDueDateMs,
  } = returnOrder;

  const statusTip = useRefundDrawerStatusTip({
    platform,
    displayStatus,
    unit,
    refundAmount,
    latestOfferAmount,
    compensationAmount,
    sellerEvidenceDeadlineMs,
    returnShipDueDateMs,
    sellerCompensationDueDateMs,
    dueDateMs,
  });

  const { statusContent } = useRefundStatusContent({
    platform,
    displayStatus,
    dueDateMs,
    unit,
    partiallyRefundAmount: partiallyRefundAmount ?? '',
    statusTip,
    isRefund: true,
  });

  const list = useMemo(
    () => [
      {
        title: t('退款单号'),
        key: 'returnSn',
        value: returnSn ? <CopyText text={returnSn} showIcon /> : null,
      },
      {
        title: t('物流单号'),
        key: 'returnTrackingNumber',
        value: returnTrackingNumber ? <CopyText text={returnTrackingNumber} showIcon /> : null,
      },
    ],
    [t, returnSn, returnTrackingNumber],
  );

  const title = statusContent?.title ?? returnStatusTip;

  return (
    <div className="grid gap-4">
      <DetailCard title={title}>
        {statusContent?.description && (
          <div className="text-[#232426] pb-1">{statusContent.description}</div>
        )}
        <InfoBox list={list} />
      </DetailCard>
    </div>
  );
};

const TikTokReturnInfo = (props: OrderReturnInfoProps & { returnStatusTip: string }) => {
  const { returnStatusTip, returnOrder, platform } = props;
  const { returnSn, returnCreateTime, dueDateMs, operate, region, internalStoreId } = returnOrder;

  const { t } = useTranslate();

  const { operatable } = useReturnItemOperatable({ platform, region, internalStoreId });

  const { tkTimeTip } = useTikTokTimeTip({
    platform,
    dueDateMs,
    operatable,
    operate,
  });

  const list = useMemo(
    () => [
      {
        title: t('退款单号'),
        key: 'returnSn',
        value: <CopyText text={returnSn} showIcon />,
      },
      {
        title: t('提交退款申请'),
        key: 'returnCreateTime',
        value: dayjs(Number(returnCreateTime)).format('YYYY-MM-DD HH:mm:ss'),
      },
    ],
    [t, returnSn, returnCreateTime],
  );

  return (
    <DetailCard
      title={
        <>
          <span>{returnStatusTip}</span>
          {tkTimeTip && (
            <span className="bg-[#FFD6D6] text-[#F05B5B] rounded-sm text-xs px-2 py-[3px] ml-2 mb-[1px] font-normal">
              {tkTimeTip.text}
            </span>
          )}
        </>
      }
    >
      <InfoBox list={list} />
    </DetailCard>
  );
};

const LazadaReturnInfo = (props: OrderReturnInfoProps & { returnStatusTip: string }) => {
  const { returnStatusTip, returnOrder } = props;
  const { t } = useTranslate();

  const { returnSn, tradeCreateTimeMs, returnCreateTime } = returnOrder;

  const list = useMemo(
    () => [
      {
        title: t('退款单号'),
        key: 'returnSn',
        value: <CopyText text={returnSn} showIcon />,
      },
      {
        title: t('ViewRefund.订购日期'),
        key: 'tradeCreateTimeMs',
        value: dayjs(Number(tradeCreateTimeMs)).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: t('ViewRefund.退货订单日期'),
        key: 'returnCreateTime',
        value: dayjs(Number(returnCreateTime)).format('YYYY-MM-DD HH:mm:ss'),
      },
    ],
    [returnCreateTime, returnSn, t, tradeCreateTimeMs],
  );

  return (
    <DetailCard title={returnStatusTip}>
      <InfoBox list={list} />
    </DetailCard>
  );
};

export const OrderReturnInfo = (props: OrderReturnInfoProps) => {
  const { platform, returnOrder } = props;
  const { displayStatus, returnStatus, returnType } = returnOrder;

  const { returnStatusTip } = useReturnItemStatusTip({
    displayStatus,
    returnStatus,
    returnType,
    platform: toUpperCase(platform),
  });

  if (platform === 'SHOPEE') {
    return (
      <ShopeeReturnInfo
        platform={platform}
        returnOrder={returnOrder}
        returnStatusTip={returnStatusTip}
      />
    );
  }

  if (isTiKTok(platform)) {
    return (
      <TikTokReturnInfo
        platform={platform}
        returnOrder={returnOrder}
        returnStatusTip={returnStatusTip}
      />
    );
  }

  if (platform === 'LAZADA') {
    return (
      <LazadaReturnInfo
        platform={platform}
        returnOrder={returnOrder}
        returnStatusTip={returnStatusTip}
      />
    );
  }

  return null;
};
